import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/slices/authSlice";
import { clearWishlist } from "../../redux/slices/wishlistSlice";
import { fetchWishlist } from "../../redux/thunks/wishlistThunks";
import Cookies from "js-cookie";
import axios from "axios";
import { io } from "socket.io-client";
import { globalLogout } from "../../Utils/Logout";
// Import your components
import MobileHeader from "./MobileHeader";
import DesktopHeader from "./DesktopHeader";
import Sidebar from "./Sidebar";
import DesktopNavigation from "./DesktopNavigation";

// New NotificationDropdown
const NotificationDropdown = ({
	notifications,
	unreadCount,
	onClearAll,
	onRemoveOne,
	onSeeAll,
}) => {
	return (
		<div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border z-50">
			<div className="flex justify-between items-center px-3 py-2 border-b">
				<h4 className="font-semibold text-gray-700">
					Notifications {unreadCount > 0 && `(${unreadCount})`}
				</h4>
				{notifications.length > 0 && (
					<button
						onClick={onClearAll}
						className="text-sm text-red-500 hover:underline"
					>
						Clear All
					</button>
				)}
			</div>

			<div className="max-h-60 overflow-y-auto">
				{notifications.length === 0 ? (
					<p className="text-gray-500 text-center py-4 text-sm">
						No notifications
					</p>
				) : (
					notifications.map((notif, index) => (
						<div
							key={index}
							className="flex justify-between items-center px-3 py-2 hover:bg-gray-50 border-b"
						>
							<span className="text-sm text-gray-700">{notif.message || "New notification"}</span>
							<button
								onClick={() => onRemoveOne(index)}
								className="text-xs text-red-400 hover:text-red-600 ml-2"
							>
								✕
							</button>
						</div>
					))
				)}
			</div>

			<div className="px-3 py-2 border-t text-center">
				<button
					onClick={onSeeAll}
					className="text-blue-600 text-sm font-medium hover:underline"
				>
					See All
				</button>
			</div>
		</div>
	);
};

const Header = () => {
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [categories, setCategories] = useState([]);
	const [loadingCategories, setLoadingCategories] = useState(true);
	const [notifications, setNotifications] = useState([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [socketStatus, setSocketStatus] = useState("disconnected");
	const [isNotifDropdownOpen, setIsNotifDropdownOpen] = useState(false);

	const location = useLocation();
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const socketRef = useRef(null);

	const { isAuthenticated, user } = useSelector((state) => state.auth);
	const { items: wishlistItems, count: wishlistCount } = useSelector((state) => state.wishlist);
	const cartItems = useSelector((state) => state.cart.items);
	const cartCount = cartItems.reduce((total, item) => total + (item.quantity || 1), 0);

	const staticCategories = [
		{ id: "delivery", name: "Living Room Furniture", path: "/delivery", published: true },
		{ id: "hotel-flight", name: "Hotel/Flight", path: "/hotel-flight", published: true },
		{ id: "service", name: "Service", path: "/service", published: true },
		{ id: "digital-showroom", name: "Digital Showroom", path: "/digital-showroom", published: true },
		{ id: "laundry", name: "Laundry", path: "/laundry", published: true },
	];

	// Socket.IO implementation
	useEffect(() => {
		if (isAuthenticated && user?.id) {
			setSocketStatus("connecting");

			const newSocket = io(process.env.REACT_APP_BACKEND_URL, {
				transports: ["websocket", "polling"],
				withCredentials: true,
				auth: { userId: user.id, userType: "Buyer" },
			});

			newSocket.on("connect", () => {
				setSocketStatus("connected");
				newSocket.emit("join", { userId: user.id, userType: "Buyer" });
			});

			newSocket.on("notification", (notification) => {
				setNotifications((prev) => [notification, ...prev]);
				setUnreadCount((prev) => prev + 1);
			});

			newSocket.on("disconnect", () => setSocketStatus("disconnected"));
			newSocket.on("connect_error", () => setSocketStatus("error"));

			socketRef.current = newSocket;

			return () => {
				if (socketRef.current) {
					socketRef.current.disconnect();
					socketRef.current = null;
					setSocketStatus("disconnected");
				}
			};
		} else if (!isAuthenticated && socketRef.current) {
			socketRef.current.disconnect();
			socketRef.current = null;
			setSocketStatus("disconnected");
			setNotifications([]);
			setUnreadCount(0);
		}
	}, [isAuthenticated, user?.id]);

	// Fetch categories
	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const response = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/seller/category/list`
				);
				if (response.data.success) {
					const apiCategories = response.data.categories
						.filter((c) => c.published)
						.map((c) => ({ id: c.id, name: c.name, path: `/category/${c.slug}`, published: c.published }));

					setCategories(apiCategories);
				}
			} catch (error) {
				console.error("Error fetching categories:", error.message);
				globalLogout(error)
			} finally {
				setLoadingCategories(false);
			}
		};
		fetchCategories();
	}, []);

	const allCategories = [...categories].filter((c) => c.published);

	useEffect(() => {
		if (isAuthenticated) {
			dispatch(fetchWishlist());
		} else {
			dispatch(clearWishlist());
			setNotifications([]);
			setUnreadCount(0);
		}
	}, [isAuthenticated, dispatch, location]);

	useEffect(() => {
		if (isSidebarOpen) document.body.style.overflow = "hidden";
		else document.body.style.overflow = "auto";
		return () => (document.body.style.overflow = "auto");
	}, [isSidebarOpen]);

	const handleLoginClick = () => {
		navigate("/login");
		closeSidebar();
	};

	const handleLogout = () => {
		dispatch(logout());
		dispatch(clearWishlist());
		Cookies.remove("ecom_user_token");
		if (socketRef.current) {
			socketRef.current.disconnect();
			socketRef.current = null;
		}
		navigate("/login");
		closeSidebar();
	};

	const closeSidebar = () => setIsSidebarOpen(false);

	const handleSearchSubmit = (query) => {
		navigate(`/search?query=${encodeURIComponent(query)}`);
	};

	const testSocketConnection = () => {
		if (socketRef.current) {
			socketRef.current.emit("test", { message: "Test from client", timestamp: Date.now() });
		}
	};

	// Notification handlers
	const handleClearAll = () => {
		setNotifications([]);
		setUnreadCount(0);
	};

	const handleRemoveOne = (index) => {
		setNotifications((prev) => prev.filter((_, i) => i !== index));
		setUnreadCount((prev) => Math.max(prev - 1, 0));
	};

	const handleSeeAll = () => {
		navigate("/notifications");
		setIsNotifDropdownOpen(false);
	};

	return (
		<div className="bg-white shadow-md relative">
			{/* Mobile Header */}
			<MobileHeader
				isAuthenticated={isAuthenticated}
				cartCount={cartCount}
				unreadCount={unreadCount}
				notifications={notifications}
				setIsSidebarOpen={setIsSidebarOpen}
				setIsNotifDropdownOpen={setIsNotifDropdownOpen}
				onSearchSubmit={handleSearchSubmit}
				navigate={navigate}
				socketStatus={socketStatus}
				onTestSocket={testSocketConnection}
				onClearAll={handleClearAll}
				onRemoveOne={handleRemoveOne}
				onSeeAll={handleSeeAll}
			/>

			{/* Desktop Header */}
			<div className="relative">
				<DesktopHeader
					isAuthenticated={isAuthenticated}
					user={user}
					wishlistCount={wishlistCount}
					cartCount={cartCount}
					unreadCount={unreadCount}
					notifications={notifications}
					onLogout={handleLogout}
					onLoginClick={handleLoginClick}
					onSearchSubmit={handleSearchSubmit}
					navigate={navigate}
					socketStatus={socketStatus}
					onTestSocket={testSocketConnection}
					setIsNotifDropdownOpen={setIsNotifDropdownOpen}
					onClearAll={handleClearAll}
					onRemoveOne={handleRemoveOne}
					onSeeAll={handleSeeAll}
				/>

				{/* Notification Dropdown */}
				{isNotifDropdownOpen && (
					<NotificationDropdown
						notifications={notifications}
						unreadCount={unreadCount}
						onClearAll={handleClearAll}
						onRemoveOne={handleRemoveOne}
						onSeeAll={handleSeeAll}
					/>
				)}
			</div>

			{/* Desktop Navigation */}
			<DesktopNavigation
				categories={allCategories}
				loadingCategories={loadingCategories}
				isAuthenticated={isAuthenticated}
				location={location}
			/>

			{/* Mobile Sidebar */}
			<Sidebar
				isOpen={isSidebarOpen}
				onClose={closeSidebar}
				isAuthenticated={isAuthenticated}
				user={user}
				categories={allCategories}
				loadingCategories={loadingCategories}
				wishlistCount={wishlistCount}
				cartCount={cartCount}
				unreadCount={unreadCount}
				location={location}
				onLoginClick={handleLoginClick}
				onLogout={handleLogout}
				navigate={navigate}
			/>
		</div>
	);
};

export default Header;
